import "./CostEstimator.css";
import * as AppConfig from '../../app/config/constants'
import Button from "../../components/elements/Button.js";
import CostEstimateApi from "../../utils/api/CostEstimateApi";
import EstimateCard from "./EstimateCard";
import EstimateDetails from "./EstimateDetails";
import Filter from "../../components/elements/Filter";
import Input from "../../components/elements/Input.js";
import Loading from "../../components/modules/Loading";
import React, {useState, useRef, useEffect, useMemo} from "react";
import axios from "axios";
import useAuthApi from "../../hooks/useAuthApi";
import useFuzzySearchQueryHandler from "./utils";
import { MemoizedUserNavigation } from "../../components/sections/Navigation";
import {update_estimator_state, EMPTY_PLANS, EMPTY_API_KEYS} from "../../state/cost_estimator";
import {useDispatch, useSelector} from "react-redux";
import {useHistory} from "react-router";


function arraysAreEqual(arr1, arr2) {
  if (arr1.length !== arr2.length) {
    return false;
  }
  return arr1.every((value, index) => value === arr2[index]);
}


async function get_member_plans(auth_api) {
  return auth_api.get('users/user_insurances/').then((data)=>{
    const user_insurances = data['user_insurances'] || []
    return user_insurances.map((ui)=>{
      const label = ui.label || `Member ID: ${ui.member_id}`
      return {
        "member_id": ui.member_id,
        "plan_name": label,
        "label": label, // used in HTML selector
      }
    })
  })
}


async function get_api_key_plans(cost_estimate_api_token) {
  /** Plans endpoint return value example
   * {
   *   "plans": [
   *     {
   *       "resource_id": "c221e7fe-c707-405d-974e-f1f7b2cb51dd",
   *       "carrier_name": "ClaimDOC",
   *       "plan_name": "Demo Plan",
   *       "plan_year": 2024,
   *       "plan_type": "hmo"
   *     },
   *     ...
   *   ]
   * }
   *
   * Here we unpack `plans` key and return just the array
   */
  if (!cost_estimate_api_token){
    return []
    // throw new Error("No valid API key provided for fetching /plans")
  }

  const data = await new CostEstimateApi(cost_estimate_api_token).get('plans')
  const plans_raw = data['plans'] || []
  // plan names may repeat. must unique-ify it for drop down
  const plans_with_labels = plans_raw.map((plan)=>{
    return {
      ...plan,
      label: `${plan.plan_name || '[No Plan Name]'} (#${plan.resource_id})`
    }
  })
  const plans = plans_with_labels.sort(
      (a, b)=>{return a.label.toLowerCase() < b.label.toLowerCase() ? -1 : +1}
  )
  return plans
}


function pick_plan_by_name(plans, name) {
  for (let plan of plans) {
    if (plan.label === name) {
      return plan
    }
  }
  return null
}

// - new_search - resets list of results
// - next_page - adds array to prior array
const NEXT_PAGE = 'next_page'
const NEW_SEARCH = 'new_search'


function CostEstimator() {

  const dispatch = useDispatch()
  const api_keys = useSelector((state)=>state.cost_estimator.api_keys)
  const api_keys_ref = useRef(EMPTY_API_KEYS)
  const api_keys_count = useSelector((state)=>state.cost_estimator.api_keys_count)
  const api_key_name_selected = useSelector((state)=>state.cost_estimator.api_key_name_selected)
  const api_key_name_selected_ref = useRef(api_key_name_selected)
  const plans = useSelector((state)=>state.cost_estimator.plans)
  const loading_api_keys = useSelector((state)=>state.cost_estimator.loading_api_keys)
  const loading_plans = useSelector((state)=>state.cost_estimator.loading_plans)
  const loading_results = useSelector((state)=>state.cost_estimator.loading_results)
  const inputs_error_message = useSelector((state)=>state.cost_estimator.inputs_error_message)

  const authApi = useAuthApi()
  // const history = useHistory()

  const plans_options = useMemo(()=>{
    return plans?.map((plan, index) => ({
      label: plan.label,
      value: index,
    })) || []
  }, [plans])

  const api_keys_options = useMemo(()=>{
    return Object.keys(api_keys).sort((a, b)=>{
      return a.toLowerCase() < b.toLowerCase() ? -1 : +1
    }).map((label)=>{
      return {
        label: label,
        value: label
      }
    })
  }, [api_keys])

  const userInputInitialState = {
    professionalNpi: "",
    institutionalNpi: "",
    zipCode: "",
    radius: "15 miles",
    plan: plans?.length ? plans[0] : null,
    service: "",
    billingCodeType: "CPT",
    specialty: "",
    cursor: null,
  };

  const [userCostEstimatorInput, setUserCostEstimatorInput] = useState(userInputInitialState);
  const [estimates, setEstimates] = useState([]);
  const [response_error_message, set_response_error_message] = useState(null);
  const [resultsInRadius, setResultsInRadius] = useState(null);
  const [IDToScrollIntoView, setIDToScrollIntoView] = useState(0); // this controls the estimate that is scrolled into view
  const newestEstimateRef = useRef(null);
  const SearchTypes = {
    npiSearch: "npiSearch",
    zipSearch: "zipSearch",
  };
  const [searchType, setSearchType] = useState(SearchTypes.zipSearch);
  const [matchingServices, setMatchingServices] = useState([]);
  const [matchingSpecialties, setMatchingSpecialties] = useState([]);

  const matching_specialties_options = useMemo(()=>{
    return Object.entries(matchingSpecialties).map(([key, value])=>{
      // our autocomplete widget does not understand label vs value. operates on labels only :(
      return {
        label: key,
        value: key
      }
    })
  }, [matchingSpecialties])

  const matching_services_options = useMemo(()=>{
    return Object.entries(matchingServices).map(([key, value])=>{
      // our autocomplete widget does not understand label vs value. operates on labels only :(
      return {
        label: key,
        value: key
      }
    })
  }, [matchingServices])

  const handleServiceNameChange = useFuzzySearchQueryHandler(
    "/services",
    setMatchingServices,
    "services",
      api_keys[api_key_name_selected]
  );

  const handleSpecialtiesNameChange = useFuzzySearchQueryHandler(
    "/specialties",
    setMatchingSpecialties,
    "specialties",
      api_keys[api_key_name_selected]
  );

  const handleApiKeySelection = React.useCallback((v) => {
    if (v !== api_key_name_selected) {
      dispatch(update_estimator_state({
        api_key_name_selected: v,
        loading_plans: true,
      }))
    }
  }, [api_key_name_selected])

  const [selectedEstimate, setSelectedEstimate] = useState(null);

  const sort_options_display = [
    { value: 'distance asc', label: 'Distance from Zip' },
    { value: 'cost asc', label: 'Price (Low to High)' },
    { value: 'cost desc', label: 'Price (High to Low)' },
    { value: 'rating desc', label: 'Rating (High to Low)' },
    { value: 'rating asc', label: 'Rating (Low to High)' },
    { value: 'name asc', label: 'Name (A-Z)' },
    { value: 'name desc', label: 'Name (Z-A)' },
  ];

  const [sort_option_selected, set_sort_option_selected] = useState(sort_options_display[0].label);
  const [sort_options, set_sort_options] = useState({ sortKey: sort_options_display[0].value.split(' ')[0], sortDirection: sort_options_display[0].value.split(' ')[1] });

  function ErrorComponent({ errorMessage }) {
    return <div className="error-message-return">{errorMessage}</div>;
  }

  function handleChange(e) {
    setUserCostEstimatorInput((prevData) => ({
      ...prevData,
      [e.target.name]: e.target.value,
    }));
  }

  function handleNextPageScroll() {
    if (!newestEstimateRef.current) return;
    newestEstimateRef.current.scrollIntoView({
      behavior: "smooth",
      block: "start",
      inline: "nearest",
    });
  }

  function clearUserInput() {
    setUserCostEstimatorInput(userInputInitialState);
  }

  function handleSearchTypeChange(e) {
    e.preventDefault();
    clearUserInput();
    setMatchingServices([]);
    setMatchingSpecialties([]);
    searchType === SearchTypes.zipSearch
      ? setSearchType(SearchTypes.npiSearch)
      : setSearchType(SearchTypes.zipSearch);
  }

  function parseInputs(userCostEstimatorInput) {
    const inputProfessionalNpi =
      userCostEstimatorInput.professionalNpi === ""
        ? null
        : parseInt(userCostEstimatorInput.professionalNpi);
    const inputInstitutionalNpi =
      userCostEstimatorInput.institutionalNpi === ""
        ? null
        : parseInt(userCostEstimatorInput.institutionalNpi);
    const inputSpecialties =
      userCostEstimatorInput.specialty === ""
        ? null
        : matchingSpecialties[userCostEstimatorInput.specialty];
    const billingCode = matchingServices[userCostEstimatorInput.service];
    return {
      inputInstitutionalNpi,
      inputProfessionalNpi,
      inputSpecialties,
      billingCode,
      zipCode: userCostEstimatorInput.zipCode,
      specialty: userCostEstimatorInput.specialty,
      service: userCostEstimatorInput.service,
    };
  }

  const handleSortChange = (label) => {
    set_sort_option_selected(label);
    const value = sort_options_display.find((option) => option.label === label).value;
    const [sortKey, sortDirection] = value.split(' ');
    set_sort_options({ sortKey, sortDirection });
  };

  const handleSubmit = async (submit_mode) => {

    // submit_mode can be of 2 types:
    // - new_search - resets list of results
    // - next_page - adds array to prior array

    setResultsInRadius(null);

    const {
      inputInstitutionalNpi,
      inputProfessionalNpi,
      inputSpecialties,
      billingCode,
      zipCode,
      service,
      specialty
    } = parseInputs(userCostEstimatorInput);

    if (
        searchType === SearchTypes.npiSearch && (
          !inputInstitutionalNpi &&
          !inputProfessionalNpi
        )
    ) {
      dispatch(update_estimator_state({
        inputs_error_message: "At least one NPI is required"
      }))

      return
    } else if (
        searchType === SearchTypes.zipSearch && (
          !zipCode || !service
        )
    ) {
      dispatch(update_estimator_state({
        inputs_error_message: "Zip and service codes required"
      }))
      return
    } else if (!billingCode) {
      dispatch(update_estimator_state({
        inputs_error_message: "Service was not matched to a valid billing code"
      }))
      return
    }

    dispatch(update_estimator_state({
      loading_results: true,
      inputs_error_message: null,
    }))
    set_response_error_message(null);
    setEstimates(null);

    const URL = AppConfig.COST_ESTIMATE_API_BASE_URI + `price/service-cost-estimator`;
    const planDetails = userCostEstimatorInput.plan?.member_id ?
        { member_id: userCostEstimatorInput.plan?.member_id } :
        { resource_id: userCostEstimatorInput.plan?.resource_id }

    axios
      .post(
        URL,
        {
          search_parameters: {
            location: {
              zip_code: userCostEstimatorInput.zipCode,
              radius_in_miles: +userCostEstimatorInput.radius.split(" ")[0],
            },
            ...(searchType === SearchTypes.zipSearch &&
              inputSpecialties && { specialties: [inputSpecialties] }),
            ...(searchType === SearchTypes.npiSearch && {
              npi_numbers: {
                professional_npi_numbers: inputProfessionalNpi
                  ? [inputProfessionalNpi]
                  : [],
                institutional_npi_numbers: inputInstitutionalNpi
                  ? [inputInstitutionalNpi]
                  : [],
              },
            }),
          },
          plan_details: planDetails,
          service_details: [
            {
              billing_code_type: "CPT",
              billing_code: billingCode,
            },
          ],
          sort: 
            [
              {
                key: sort_options.sortKey,
                direction: sort_options.sortDirection
              }
            ]
          ,
          pagination: {
            count: 10,
            // If the caller is the main form submit,
            // then don't send back the cursor as that is not
            // a "show more estimates" call
            ...(submit_mode !== NEW_SEARCH &&
              userCostEstimatorInput.cursor && {
                cursor: userCostEstimatorInput.cursor,
              }),
          },
        },
        {
          headers: {
            "x-api-key": `${api_keys[api_key_name_selected]}`,
            "Content-Type": "application/json",
          },
        }
      )
      .then((res) => {
        if (searchType === SearchTypes.zipSearch) {
          setUserCostEstimatorInput((prevData) => ({
            ...prevData,
            cursor: res.data.cursor,
          }))
        }
        // If the caller is the "see more estimates" button
        // merge the old estimates with the new estimates
        // Once there is pagination for NPI search, this will become the default
        if (submit_mode !== NEW_SEARCH) {
          const updatedEstimates = [...estimates, ...res.data.estimates];
          setIDToScrollIntoView(estimates.length - 4);
          setEstimates(updatedEstimates);
          handleNextPageScroll();
        } else {
          setEstimates(res.data.estimates);
        }
        set_response_error_message(null);
        setResultsInRadius(
          `Results for CPT ${
            matchingServices[userCostEstimatorInput.service]
          } within ${userCostEstimatorInput.radius} of ${
            userCostEstimatorInput.zipCode
          }`
        );
      })
      .catch((error) => {
        set_response_error_message(error.response?.data?.error);
        setEstimates(null);
      })
      .finally(() => {
        dispatch(update_estimator_state({loading_results: false}))
      });
  };

  const specialtyToServiceMap = {
    'Gastroenterology Physician': {billingCode: '45378', service: 'Colonoscopy'},
    'Dermatologists': {billingCode: '10040', service: 'Acne Surgery'},
    'Dermatology Physician': {billingCode: '10040', service: 'Acne Surgery'},
    'Orthopedic Surgeons': {billingCode: '64721', service: 'Carpal Tunnel Surgery'},
    'Hospitals': {billingCode: '80048', service: 'Basic Metabolic Panel'},
    'Ambulatory Surgical Clinic/Center': {billingCode: '80048', service: 'Basic Metabolic Panel'},
    'Urgent Care Clinic/Center': {billingCode: '80048', service: 'Basic Metabolic Panel'},
    'Laboratories': {billingCode: '80048', service: 'Basic Metabolic Panel'},
    'Physical Rehabilitation Specialists': {billingCode: '80048', service: 'Basic Metabolic Panel'},
    'Imaging Centers': {billingCode: '72141', service: 'MRI of upper spine without contrast'},
  }

  // -------------- useEffects start here -------------

  useEffect(() => {

    if (!loading_api_keys) return

    const old_api_key_name_selected = api_key_name_selected_ref.current
    if (old_api_key_name_selected in (api_keys_ref.current || {})) {
      dispatch(update_estimator_state({
        loading_api_keys: false,
      }))
      return
    }

    const sorted_api_key_names = (api_keys)=>{ return Object.keys(api_keys).sort((a, b)=>{return a < b ? -1 : 1})}

    authApi.get('users/credentials').then((data)=>{
      const v = data['provider_directory_credentials'] || {}
      // // for testing
      // if (Object.keys(v).length) {
      //   const key = Object.keys(v)[0]
      //   return {key: v[key]}
      // } else {
      //   return v
      // }
      return v
    }).then((new_api_keys)=>{
      if (Object.keys(new_api_keys).length === 0) {
        dispatch(update_estimator_state({
          api_keys: EMPTY_API_KEYS,
          api_keys_count: 0,
          api_key_name_selected: null,
          plans: EMPTY_PLANS,
          loading_api_keys: false,
          loading_plans: false,
          inputs_error_message: "Profile not linked any active plans. Contact customer support.",
        }))
        api_keys_ref.current = EMPTY_API_KEYS
        return
      }

      if (old_api_key_name_selected in new_api_keys) {
        const old_api_keys = sorted_api_key_names(api_keys)
        const new_api_keys = sorted_api_key_names(new_api_keys)
        if (!arraysAreEqual(old_api_keys, new_api_keys)) {
          dispatch(update_estimator_state({
            api_keys: new_api_keys,
            api_keys_count: Object.keys(new_api_keys).length,
            loading_api_keys: false,  // nothing else to do
            loading_plans: false, // do not need to reload as key is same as with previous run and plans are likely same
          }))
          api_keys_ref.current = new_api_keys
          // plans did not change
          // selected key did not change
        }
        return
      }

      // if we are here, we need to pick new key name
      const new_api_key_name = sorted_api_key_names(new_api_keys)[0]
      const new_api_keys_count = Object.keys(new_api_keys).length
      dispatch(update_estimator_state({
        api_keys: new_api_keys_count ? new_api_keys : EMPTY_API_KEYS,
        api_keys_count: new_api_keys_count,
        api_key_name_selected: new_api_key_name,
        loading_api_keys: false,
        plans: EMPTY_PLANS,
        loading_plans: true,  // <--- !!!!! triggers reload in next useEffect !!!!!
      }))
      api_keys_ref.current = new_api_keys
    }).catch((e)=>{
      console.log('Error while fetching credentials: ', e)
      dispatch(update_estimator_state({
        // reset everything
        api_keys: EMPTY_API_KEYS,
        api_keys_count: 0,
        api_key_name_selected: null,
        plans: EMPTY_PLANS,
        loading_api_keys: false,
        loading_plans: false,
      }))
      api_keys_ref.current = EMPTY_API_KEYS
      // history.push({
      //   pathname: '/signout',
      // })
    })
  }, [loading_api_keys]);

  useEffect(() => {

    if (!loading_plans) return

    if (!api_key_name_selected) {
      dispatch(update_estimator_state({
        plans: EMPTY_PLANS,
        loading_plans: false,
      }))
    }

    // Even though fetching user_insurances (member ids) does not require API Key
    // fetching Cost Estimates per that member_id DOES require API key.
    // So, we are fetching member IDs here along with API-Key-based plans
    // after API Key fetch because without API Key fetch complete, member ids are meaningless

    const plans_promise = Promise.all([
        get_member_plans(authApi),
        get_api_key_plans(api_keys[api_key_name_selected]),
    ]).then((plans_arrays)=>{
      const member_plans = plans_arrays[0]
      const api_key_plans = plans_arrays[1]
      return member_plans.concat(api_key_plans)
    })

    plans_promise.then((plans)=>{
      dispatch(update_estimator_state({
        plans: plans.length ? plans : EMPTY_PLANS,
        loading_plans: false,
      }))
      setUserCostEstimatorInput((prevData) => ({
        ...prevData,
        plan: plans.length ? plans[0] : null,
      }))
    }).catch((e)=>{
      console.log(e)
      dispatch(update_estimator_state({
        plans: EMPTY_PLANS,
        loading_plans: false,
      }))
    })
  }, [loading_plans, setUserCostEstimatorInput]);

  const clearSpecialtyInput = React.useCallback(()=>{
    setUserCostEstimatorInput((prevData) => ({
      ...prevData,
      specialty: "",
    }));
    setMatchingSpecialties([]);
  }, [])

  const clearServiceInput = React.useCallback(()=>{
    setUserCostEstimatorInput((prevData) => ({
      ...prevData,
      service: "",
    }));
    setMatchingServices([]);
  }, [])

  // -------------- return value starts here -------------

  if (!selectedEstimate && (loading_api_keys || loading_plans)) {
    return (<Loading/>)
  }

  // Bootstrap grid docs:
  // https://getbootstrap.com/docs/4.6/layout/grid/

  //when selected estimate is NOT null, the EstimateDetails is called and is rendered with selectedEstimate.
  //if selectedEstimate is null, then we will continue to show the results page
  //selectedEstimate holds the info of the card that we selected therefore passing through to estimate details for it to be shown
  return (
      <div>
        <MemoizedUserNavigation />
        {selectedEstimate ? (
            <EstimateDetails
                selectedEstimate={selectedEstimate}
                setSelectedEstimate={setSelectedEstimate}
            />
        ) : (
            <div className="cost-page-content">
              {inputs_error_message && (
                  <div className="alert-parent" style={{zIndex: 1000}}>
                    <div className="alert-child">
                      <div className="alert alert-danger" role="alert">
                        <span>{inputs_error_message}</span>&nbsp;
                        <button
                            type="button"
                            className="close"
                            data-dismiss="alert"
                            aria-label="Close"
                            onClick={() => {
                              dispatch(update_estimator_state({
                                inputs_error_message: null
                              }))
                            }}
                        >
                          <span aria-hidden="true">&times;</span>
                        </button>
                      </div>
                    </div>
                  </div>
              )}
              <div className="container text-center" style={{zIndex: 0}}>
                <div className="row row-padding">
                  <div className="col">
                    <div className="btn-group btn-group-toggle" data-toggle="buttons">
                      <Button
                          label="Searching by"
                          className="btn btn-light"
                          active={true}
                          onClick={handleSearchTypeChange}
                      />
                      <Button
                          label={searchType === SearchTypes.zipSearch ? "Specialty" : "Provider ID"}
                          className="btn btn-secondary dropdown-toggle"
                          active={true}
                          onClick={handleSearchTypeChange}
                      ></Button>
                    </div>
                  </div>
                </div>
                <div className="row row-padding">
                  {searchType === "zipSearch" && (
                      <div className="col">
                        <Input
                            placeholder="Specialty"
                            name="specialties"
                            label="Specialty"
                            value={userCostEstimatorInput.specialty}
                            onChange={(event) => {
                              const val = event.target.value;
                              setUserCostEstimatorInput((prevData) => ({
                                ...prevData,
                                specialty: val,
                              }));
                              handleSpecialtiesNameChange(event);
                            }}
                            onSelect={(val, name) => {
                              const correspondingService = specialtyToServiceMap[val];
                              setUserCostEstimatorInput((prevData) => ({
                                ...prevData,
                                specialty: val,
                                service: correspondingService?.service || prevData.service,
                              }))
                              if (correspondingService) {
                                setMatchingServices({[correspondingService.service]: correspondingService.billingCode});
                              }
                            }}
                            showArrow={false}
                            readOnly={false}
                            options={matching_specialties_options}
                            forceAutocomplete={true}
                            required={false}
                            showAllOptions={true}
                        />
                        {
                            userCostEstimatorInput.specialty &&
                            <button onClick={clearSpecialtyInput} style={{
                              position: 'absolute',
                              right: '1px',
                              top: '50%',
                              background: 'none',
                              border: 'none',
                              cursor: 'pointer',
                              fontSize: '20px',
                              color: '#888'}}>
                              &times;
                            </button>
                        }
                      </div>
                  )}
                  {searchType === SearchTypes.npiSearch && (
                      <React.Fragment>
                        <div className="col">
                          <Input
                              placeholder="Professional NPI"
                              name="professionalNpi"
                              label="Professional NPI"
                              value={userCostEstimatorInput.professionalNpi}
                              onChange={handleChange}
                              required={false}
                          />
                        </div>
                        <div className="col">
                          <Input
                              placeholder="Institutional NPI"
                              name="institutionalNpi"
                              value={userCostEstimatorInput.institutionalNpi}
                              onChange={handleChange}
                              label="Institutional NPI"
                              required={false}
                          />
                        </div>
                      </React.Fragment>
                  )}
                </div>
                <div className="row row-padding">
                  <div className="col">
                    <Input
                        placeholder="Service"
                        name="service"
                        label="Service"
                        value={userCostEstimatorInput.service}
                        onChange={(event) => {
                          const val = event.target.value;
                          setUserCostEstimatorInput((prevData) => ({
                            ...prevData,
                            service: val,
                          }));
                          handleServiceNameChange(event);
                        }}
                        onSelect={(val) =>
                            setUserCostEstimatorInput((prevData) => ({
                              ...prevData,
                              service: val,
                            }))
                        }
                        showArrow={false}
                        readOnly={false}
                        options={matching_services_options}
                        forceAutocomplete={true}
                        showAllOptions={true}
                    />
                    {
                        userCostEstimatorInput.service &&
                        <button onClick={clearServiceInput} style={{
                          position: 'absolute',
                          right: '1px',
                          top: '50%',
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          fontSize: '20px',
                          color: '#888'}}>
                          &times;
                        </button>
                    }

                  </div>
                </div>
                <div className="row row-padding">
                  <div className="col align-self-end">
                    <Input
                        placeholder="Zip Code"
                        name="zipCode"
                        value={userCostEstimatorInput.zipCode}
                        onChange={handleChange}
                        label="Zip Code"
                        required
                    />
                  </div>
                  <div className="col align-self-end">
                    <Input
                        showAllOptions
                        selectOnly
                        showDropdownArrow
                        placeholder="Radius"
                        name="radius"
                        label="Radius"
                        value={userCostEstimatorInput.radius}
                        onChange={handleChange}
                        onSelect={(val) =>
                            setUserCostEstimatorInput((prevData) => ({
                              ...prevData,
                              radius: val,
                            }))
                        }
                        inputContainerClasses="access-code_input"
                        showArrow={false}
                        readOnly={false}
                        options={[
                          {val: 15, label: "15 miles"},
                          {val: 25, label: "25 miles"},
                          {val: 35, label: "35 miles"},
                          {val: 50, label: "50 miles"}
                        ]}
                        required
                    />
                  </div>
                  <div className="col align-self-end">
                    <Input
                        showAllOptions
                        selectOnly
                        showDropdownArrow
                        placeholder="Sort Results By"
                        name="sort_order"
                        label="Sort Results By"
                        value={sort_option_selected}
                        onSelect={handleSortChange}
                        inputContainerClasses="access-code_input"
                        showArrow={false}
                        readOnly={false}
                        options={sort_options_display}
                        required
                    />
                  </div>
                </div>
                {
                    api_keys_count > 0 && (
                        <div className="row row-padding row-section-start">
                          <div className="col align-self-end">
                            {api_keys_count === 1 ? null :
                                api_keys_count === 0 ?
                                    <span className="alert alert-danger" role="alert">
                      No Employer linked. Contact Customer Support.
                    </span>
                                    :
                                    <Input
                                        showAllOptions
                                        selectOnly
                                        showDropdownArrow
                                        placeholder="Api Key"
                                        name="api_key"
                                        label={<span>{api_keys_count === 0 ? 'No API Keys Provisioned' : 'API Key'}</span>}
                                        value={api_key_name_selected || ''}
                                        onSelect={handleApiKeySelection}
                                        showArrow={false}
                                        readOnly={api_keys_count === 0}
                                        options={api_keys_count === 0 ? [] : api_keys_options}
                                        required
                                    />
                            }
                          </div>
                          <div className="col align-self-end">
                            <Input
                                showAllOptions
                                selectOnly
                                showDropdownArrow
                                placeholder="Plan"
                                name="plan"
                                label="Plan"
                                value={userCostEstimatorInput.plan?.label || ''}
                                onSelect={(val) => {
                                  setUserCostEstimatorInput((prevData) => ({
                                    ...prevData,
                                    plan: pick_plan_by_name(plans, val),
                                  }))
                                }}
                                showArrow={false}
                                readOnly={!(plans.length && api_keys_count)}
                                options={plans_options}
                                required
                            />
                          </div>
                        </div>
                    )
                }
                <div className="row row-padding row-section-start">
                  <Button
                      active={plans.length && api_keys_count}
                      // class="long-btn filled"
                      className="btn btn-primary"
                      name="new_search"
                      label="Search"
                      onClick={()=>{handleSubmit(NEW_SEARCH)}}
                  ></Button>
                </div>
              </div>

              <div className="results-module-section">
                <div>
                  <p className="results-bar"> {resultsInRadius} </p>{" "}
                </div>
                {loading_results && <Loading flexStartLoading/>}
                {response_error_message && <ErrorComponent errorMessage={response_error_message}/>}
                {estimates &&
                    estimates.map((estimate, idx) => (
                        <EstimateCard
                            key={idx}
                            estimate={estimate}
                            idx={idx}
                            customRef={
                              idx === IDToScrollIntoView ? newestEstimateRef : null
                            }
                            renderOONTags={true}
                            setSelectedEstimate={setSelectedEstimate}
                        />
                    ))}
                {userCostEstimatorInput.cursor && estimates && (
                    <div style={{marginTop: "32px"}}>
                      <Button
                          class="text-btn"
                          label="See more estimates"
                          name="next_page"
                          onClick={()=>{handleSubmit(NEXT_PAGE)}}
                          active={userCostEstimatorInput.cursor}
                      />
                    </div>
                )}
              </div>
            </div>
        )}
      </div>
  );
}

export default CostEstimator;
